const BYTES_PER_U32 = 4;
const U32_MAX = 0xffff_ffff;

/**
 * One accepted contact can contribute at most one reference to each endpoint.
 * Static endpoints are omitted at build time, so 2R is an exact conservative
 * capacity rather than a per-body heuristic.
 */
export const CONTACT_ONLY_CSR_ENDPOINTS_PER_CONTACT = 2;

export interface ContactOnlyCsrLayoutInput {
  maxBodies: number;
  contactRowCapacity: number;
}

export interface ContactOnlyCsrDeviceLimits {
  maxStorageBufferBindingSize: number;
  maxBufferSize: number;
}

export interface ContactOnlyCsrRange {
  offset: number;
  length: number;
  end: number;
  bytes: number;
}

export interface ContactOnlyCsrLimitStatus {
  checked: boolean;
  ok: boolean;
  maxStorageBufferBindingSize?: number;
  maxBufferSize?: number;
  exceedsMaxStorageBufferBindingSize: boolean;
  exceedsMaxBufferSize: boolean;
}

/**
 * Stable ABI for the final, solver-readable contact-only CSR allocation.
 *
 * Keeping all three ranges in one u32 storage buffer gives the strict AVBD
 * body solver a single adjacency binding. Counts are retained for consumers
 * that already use them, while offsets remove the old fixed degree cap and
 * linked overflow traversal.
 */
export interface ContactOnlyCsrLayout {
  maxBodies: number;
  contactRowCapacity: number;
  endpointCapacity: number;
  bodyCounts: ContactOnlyCsrRange;
  bodyOffsets: ContactOnlyCsrRange;
  contactRefs: ContactOnlyCsrRange;
  totalWords: number;
  totalBytes: number;
  limitStatus: ContactOnlyCsrLimitStatus;
}

/** CPU/reference descriptor; canonicalOrdinal is the stable active-row order. */
export interface CanonicalContactOnlyCsrRow {
  canonicalOrdinal: number;
  contactRow: number;
  bodyA: number;
  bodyB: number;
  dynamicA: boolean;
  dynamicB: boolean;
}

export interface ContactOnlyCsrReferenceBuild {
  layout: ContactOnlyCsrLayout;
  words: Uint32Array;
  activeEndpointCount: number;
}

export interface ContactOnlyCsrSelfTestResult {
  ok: boolean;
  checks: number;
  failures: string[];
  coliseum510k: {
    maxBodies: number;
    contactRowCapacity: number;
    endpointCapacity: number;
    totalWords: number;
    totalBytes: number;
  };
}

function nonNegativeSafeInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative safe integer (received ${String(value)}).`);
  }
  return value;
}

function positiveSafeInteger(value: number, label: string): number {
  const result = nonNegativeSafeInteger(value, label);
  if (result < 1) throw new Error(`${label} must be positive.`);
  return result;
}

function checkedProduct(label: string, ...factors: number[]): number {
  let result = 1;
  for (const factor of factors) {
    result *= factor;
    if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  }
  return result;
}

function checkedSum(label: string, ...terms: number[]): number {
  const result = terms.reduce((sum, term) => sum + term, 0);
  if (!Number.isSafeInteger(result)) throw new Error(`${label} exceeds exact integer range.`);
  return result;
}

function appendRange(cursor: number, length: number, label: string): ContactOnlyCsrRange {
  const end = checkedSum(`${label} end`, cursor, length);
  return Object.freeze({
    offset: cursor,
    length,
    end,
    bytes: checkedProduct(`${label} byte length`, length, BYTES_PER_U32),
  });
}

function normalizedLimit(value: number, label: string): number {
  const result = positiveSafeInteger(value, label);
  return result;
}

/**
 * Plans the final CSR allocation without touching WebGPU or allocating a
 * TypedArray. Unlike the current pair ABI, this layout has no 16-bit body-ID
 * assumption and is valid for the 510K target profile.
 */
export function planContactOnlyCsrLayout(
  requested: ContactOnlyCsrLayoutInput,
  limits?: ContactOnlyCsrDeviceLimits,
): ContactOnlyCsrLayout {
  const maxBodies = positiveSafeInteger(requested.maxBodies, 'maxBodies');
  const contactRowCapacity = positiveSafeInteger(
    requested.contactRowCapacity,
    'contactRowCapacity',
  );
  if (maxBodies > U32_MAX) {
    throw new Error(`maxBodies exceeds the exact u32 endpoint ABI (${maxBodies}).`);
  }
  const endpointCapacity = checkedProduct(
    'contact-only CSR endpoint capacity',
    contactRowCapacity,
    CONTACT_ONLY_CSR_ENDPOINTS_PER_CONTACT,
  );
  if (endpointCapacity > U32_MAX) {
    throw new Error(
      `contact-only CSR endpoint capacity ${endpointCapacity} exceeds the u32 offset ABI.`,
    );
  }

  let cursor = 0;
  const bodyCounts = appendRange(cursor, maxBodies, 'bodyCounts');
  cursor = bodyCounts.end;
  const bodyOffsets = appendRange(cursor, checkedSum('body offset count', maxBodies, 1), 'bodyOffsets');
  cursor = bodyOffsets.end;
  const contactRefs = appendRange(cursor, endpointCapacity, 'contactRefs');
  cursor = contactRefs.end;
  if (cursor > U32_MAX) {
    throw new Error(`contact-only CSR buffer has ${cursor} words and cannot be indexed by u32.`);
  }
  const totalBytes = checkedProduct('contact-only CSR byte length', cursor, BYTES_PER_U32);

  let limitStatus: ContactOnlyCsrLimitStatus = {
    checked: false,
    ok: true,
    exceedsMaxStorageBufferBindingSize: false,
    exceedsMaxBufferSize: false,
  };
  if (limits) {
    const maxStorageBufferBindingSize = normalizedLimit(
      limits.maxStorageBufferBindingSize,
      'maxStorageBufferBindingSize',
    );
    const maxBufferSize = normalizedLimit(limits.maxBufferSize, 'maxBufferSize');
    const exceedsMaxStorageBufferBindingSize = totalBytes > maxStorageBufferBindingSize;
    const exceedsMaxBufferSize = totalBytes > maxBufferSize;
    limitStatus = {
      checked: true,
      ok: !exceedsMaxStorageBufferBindingSize && !exceedsMaxBufferSize,
      maxStorageBufferBindingSize,
      maxBufferSize,
      exceedsMaxStorageBufferBindingSize,
      exceedsMaxBufferSize,
    };
  }

  return Object.freeze({
    maxBodies,
    contactRowCapacity,
    endpointCapacity,
    bodyCounts,
    bodyOffsets,
    contactRefs,
    totalWords: cursor,
    totalBytes,
    limitStatus: Object.freeze(limitStatus),
  });
}

function checkedU32(value: number, label: string): number {
  const result = nonNegativeSafeInteger(value, label);
  if (result > U32_MAX) throw new Error(`${label} exceeds u32 (${result}).`);
  return result;
}

function validateReferenceRow(
  row: CanonicalContactOnlyCsrRow,
  layout: ContactOnlyCsrLayout,
): void {
  checkedU32(row.canonicalOrdinal, 'canonicalOrdinal');
  const contactRow = checkedU32(row.contactRow, 'contactRow');
  const bodyA = checkedU32(row.bodyA, 'bodyA');
  const bodyB = checkedU32(row.bodyB, 'bodyB');
  if (contactRow >= layout.contactRowCapacity) {
    throw new Error(`contactRow=${contactRow} exceeds contactRowCapacity=${layout.contactRowCapacity}.`);
  }
  if (bodyA >= layout.maxBodies || bodyB >= layout.maxBodies) {
    throw new Error(`contact endpoints (${bodyA}, ${bodyB}) exceed maxBodies=${layout.maxBodies}.`);
  }
  if (bodyA === bodyB) throw new Error(`contactRow=${contactRow} is a self-contact.`);
}

/**
 * GPU-free reference build used by exactness tests for the future GPU path.
 *
 * Rows are sorted by their caller-supplied canonical ordinal before scatter,
 * so the final per-body order is independent of worker/dispatch completion
 * order. A GPU implementation must preserve this same order (for example by
 * a stable integer sort of endpoint tokens); atomic cursor scatter alone is
 * not an equivalent implementation.
 */
export function buildContactOnlyCsrReference(
  layout: ContactOnlyCsrLayout,
  rows: readonly CanonicalContactOnlyCsrRow[],
): ContactOnlyCsrReferenceBuild {
  const canonicalRows = [...rows];
  const seenOrdinals = new Set<number>();
  const seenContactRows = new Set<number>();
  for (const row of canonicalRows) {
    validateReferenceRow(row, layout);
    if (seenOrdinals.has(row.canonicalOrdinal)) {
      throw new Error(`Duplicate canonicalOrdinal=${row.canonicalOrdinal}.`);
    }
    if (seenContactRows.has(row.contactRow)) {
      throw new Error(`Duplicate active contactRow=${row.contactRow}.`);
    }
    seenOrdinals.add(row.canonicalOrdinal);
    seenContactRows.add(row.contactRow);
  }
  canonicalRows.sort((a, b) => a.canonicalOrdinal - b.canonicalOrdinal);

  const words = new Uint32Array(layout.totalWords);
  let activeEndpointCount = 0;
  for (const row of canonicalRows) {
    if (row.dynamicA) {
      words[layout.bodyCounts.offset + row.bodyA]++;
      activeEndpointCount++;
    }
    if (row.dynamicB) {
      words[layout.bodyCounts.offset + row.bodyB]++;
      activeEndpointCount++;
    }
  }
  if (activeEndpointCount > layout.endpointCapacity) {
    throw new Error(
      `Active endpoint count ${activeEndpointCount} exceeds CSR capacity ${layout.endpointCapacity}.`,
    );
  }

  let prefix = 0;
  const cursors = new Uint32Array(layout.maxBodies);
  for (let body = 0; body < layout.maxBodies; body++) {
    words[layout.bodyOffsets.offset + body] = prefix;
    cursors[body] = prefix;
    prefix += words[layout.bodyCounts.offset + body];
  }
  words[layout.bodyOffsets.offset + layout.maxBodies] = prefix;
  if (prefix !== activeEndpointCount) {
    throw new Error(`CSR prefix sum ${prefix} does not match endpoint count ${activeEndpointCount}.`);
  }

  const append = (body: number, contactRow: number): void => {
    const destination = cursors[body]++;
    if (destination >= activeEndpointCount) {
      throw new Error(`CSR scatter destination ${destination} exceeds active endpoint count.`);
    }
    words[layout.contactRefs.offset + destination] = contactRow;
  };
  for (const row of canonicalRows) {
    if (row.dynamicA) append(row.bodyA, row.contactRow);
    if (row.dynamicB) append(row.bodyB, row.contactRow);
  }

  return { layout, words, activeEndpointCount };
}

/** Returns one body's final contact-row sequence without exposing unused capacity. */
export function readContactOnlyCsrBodyRefs(
  build: ContactOnlyCsrReferenceBuild,
  body: number,
): Uint32Array {
  if (!Number.isSafeInteger(body) || body < 0 || body >= build.layout.maxBodies) {
    throw new Error(`body=${String(body)} is outside the CSR body range.`);
  }
  const start = build.words[build.layout.bodyOffsets.offset + body];
  const end = build.words[build.layout.bodyOffsets.offset + body + 1];
  return build.words.slice(
    build.layout.contactRefs.offset + start,
    build.layout.contactRefs.offset + end,
  );
}

/** Deterministic, allocation-light proof of the ABI and canonical-order contract. */
export function runContactOnlyCsrSelfTests(): ContactOnlyCsrSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };

  const target = planContactOnlyCsrLayout(
    { maxBodies: 510_000, contactRowCapacity: 7_340_032 },
    {
      maxStorageBufferBindingSize: 128 * 1024 * 1024,
      maxBufferSize: 256 * 1024 * 1024,
    },
  );
  check('510K endpoint capacity changed', target.endpointCapacity === 14_680_064);
  check(
    '510K range ABI changed',
    target.bodyCounts.offset === 0
      && target.bodyCounts.length === 510_000
      && target.bodyOffsets.offset === 510_000
      && target.bodyOffsets.length === 510_001
      && target.contactRefs.offset === 1_020_001
      && target.contactRefs.length === 14_680_064,
  );
  check('510K total word count changed', target.totalWords === 15_700_065);
  check('510K total byte count changed', target.totalBytes === 62_800_260);
  check('510K CSR must fit one 128 MiB binding', target.limitStatus.ok);

  const exactLimit = planContactOnlyCsrLayout(
    { maxBodies: 510_000, contactRowCapacity: 7_340_032 },
    { maxStorageBufferBindingSize: 62_800_260, maxBufferSize: 62_800_260 },
  );
  const oneWordShort = planContactOnlyCsrLayout(
    { maxBodies: 510_000, contactRowCapacity: 7_340_032 },
    { maxStorageBufferBindingSize: 62_800_256, maxBufferSize: 62_800_260 },
  );
  check('CSR exact binding boundary must pass', exactLimit.limitStatus.ok);
  check(
    'CSR one-word binding overflow classification changed',
    !oneWordShort.limitStatus.ok
      && oneWordShort.limitStatus.exceedsMaxStorageBufferBindingSize
      && !oneWordShort.limitStatus.exceedsMaxBufferSize,
  );

  const smallLayout = planContactOnlyCsrLayout({ maxBodies: 5, contactRowCapacity: 12 });
  const canonicalRows: CanonicalContactOnlyCsrRow[] = [
    { canonicalOrdinal: 10, contactRow: 7, bodyA: 1, bodyB: 2, dynamicA: true, dynamicB: true },
    { canonicalOrdinal: 2, contactRow: 1, bodyA: 0, bodyB: 4, dynamicA: false, dynamicB: false },
    { canonicalOrdinal: 12, contactRow: 9, bodyA: 3, bodyB: 4, dynamicA: true, dynamicB: false },
    { canonicalOrdinal: 4, contactRow: 2, bodyA: 2, bodyB: 4, dynamicA: true, dynamicB: false },
    { canonicalOrdinal: 7, contactRow: 5, bodyA: 1, bodyB: 3, dynamicA: true, dynamicB: true },
  ];
  const shuffled = [
    canonicalRows[2],
    canonicalRows[0],
    canonicalRows[4],
    canonicalRows[1],
    canonicalRows[3],
  ];
  const buildA = buildContactOnlyCsrReference(smallLayout, canonicalRows);
  const buildB = buildContactOnlyCsrReference(smallLayout, shuffled);
  check('canonical CSR endpoint coverage changed', buildA.activeEndpointCount === 6);
  check(
    'canonical CSR depends on input/worker completion order',
    buildA.words.length === buildB.words.length
      && buildA.words.every((word, index) => word === buildB.words[index]),
  );
  check(
    'body 1 canonical contact order changed',
    [...readContactOnlyCsrBodyRefs(buildA, 1)].join(',') === '5,7',
  );
  check(
    'body 2 canonical contact order changed',
    [...readContactOnlyCsrBodyRefs(buildA, 2)].join(',') === '2,7',
  );
  check(
    'body 3 canonical contact order changed',
    [...readContactOnlyCsrBodyRefs(buildA, 3)].join(',') === '5,9',
  );
  check(
    'static endpoints entered canonical adjacency',
    readContactOnlyCsrBodyRefs(buildA, 0).length === 0
      && readContactOnlyCsrBodyRefs(buildA, 4).length === 0,
  );

  const wideLayout = planContactOnlyCsrLayout({ maxBodies: 65_538, contactRowCapacity: 1 });
  const wideBuild = buildContactOnlyCsrReference(wideLayout, [{
    canonicalOrdinal: 0,
    contactRow: 0,
    bodyA: 65_536,
    bodyB: 65_537,
    dynamicA: true,
    dynamicB: true,
  }]);
  check(
    'CSR body identity is still truncated to 16 bits',
    [...readContactOnlyCsrBodyRefs(wideBuild, 65_536)].join(',') === '0'
      && [...readContactOnlyCsrBodyRefs(wideBuild, 65_537)].join(',') === '0'
      && readContactOnlyCsrBodyRefs(wideBuild, 0).length === 0,
  );

  let duplicateRejected = false;
  try {
    buildContactOnlyCsrReference(smallLayout, [canonicalRows[0], {
      ...canonicalRows[3],
      canonicalOrdinal: canonicalRows[0].canonicalOrdinal,
    }]);
  } catch {
    duplicateRejected = true;
  }
  check('duplicate canonical order was accepted', duplicateRejected);

  let oversizedOffsetsRejected = false;
  try {
    planContactOnlyCsrLayout({ maxBodies: 1, contactRowCapacity: 0x8000_0000 });
  } catch {
    oversizedOffsetsRejected = true;
  }
  check('CSR endpoint capacity beyond u32 offsets was accepted', oversizedOffsetsRejected);

  return {
    ok: failures.length === 0,
    checks,
    failures,
    coliseum510k: {
      maxBodies: target.maxBodies,
      contactRowCapacity: target.contactRowCapacity,
      endpointCapacity: target.endpointCapacity,
      totalWords: target.totalWords,
      totalBytes: target.totalBytes,
    },
  };
}
